import { EditorGrid, EditorSectionHeading } from '@components/common/EditorWorkspace'
import React, { useEffect, useMemo, useState } from 'react'
import ViewNavigation from '@components/common/ViewNavigation'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Autocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import SaveIcon from '@mui/icons-material/Save'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { GlassButton, GlassCard, GlassOutlineButton, GlassPanel } from '@components/common/Glass'
import {
  altColor,
  altColorDarkText,
  helperTextColor,
  mainColor,
  mainColorMediumText,
  displayFont,
  shadow1,
} from 'src/ui/theme/tokens'
import { split } from 'src/utils/functions'
import { deleteList, getListCategories, getListTags, saveList } from 'src/api/lists'
import { List as ListType } from 'src/api/types/lists'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import MenuItem from '@mui/material/MenuItem'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Alert, { AlertInput } from '@components/common/Alert'

type EditListViewProps = {
  list: ListType
  onClose?: () => void
  onOpenHowToPlay?: () => void
  onSaved?: (list: ListType) => void
  onDeleted?: (listId: number) => void
}

const normalizeItems = (items: NonNullable<ListType['items']>) =>
  items.map((item, i) => ({
    ...item,
    index: item.index || item.rank || i + 1,
  }))

const sortItems = (items: NonNullable<ListType['items']>) =>
  items.slice().sort((a, b) => (a.index || 0) - (b.index || 0))

const EditListView: React.FC<EditListViewProps> = ({
  list,
  onClose,
  onOpenHowToPlay,
  onSaved,
  onDeleted,
}) => {
  const [editList, setEditList] = useState<ListType>(() => ({
    ...list,
    items: list.items ? sortItems(normalizeItems(list.items)) : [],
  }))
  const [categoryOptions, setCategoryOptions] = useState<string[]>([])
  const [tagOptions, setTagOptions] = useState<string[]>([])
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [savedMsg, setSavedMsg] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveAlert, setSaveAlert] = useState<AlertInput>({ open: false })
  const [visibility, setVisibility] = useState<'public' | 'private'>(
    list.private ? 'private' : 'public'
  )
  const forcedPrivateByModeration =
    editList.moderationStatus === 'blocked' || editList.moderationStatus === 'offensive'

  const selectedCategories = useMemo(
    () => (editList.category ? split(editList.category, ',') : []),
    [editList.category]
  )
  const selectedTags = useMemo(
    () => (editList.tags ? split(editList.tags, ',') : []),
    [editList.tags]
  )
  const mergedCategoryOptions = useMemo(
    () => Array.from(new Set([...(categoryOptions || []), ...selectedCategories])).filter(Boolean),
    [categoryOptions, selectedCategories]
  )
  const mergedTagOptions = useMemo(
    () => Array.from(new Set([...(tagOptions || []), ...selectedTags])).filter(Boolean),
    [tagOptions, selectedTags]
  )
  const normalizeMetaValue = (value: string) => value.trim().slice(0, 16)
  const normalizeMetaList = (value: string[]) =>
    value.map(normalizeMetaValue).filter(Boolean).slice(0, 3)

  useEffect(() => {
    if (!categoryOptions.length) {
      getListCategories()
        .then((res) => {
          if (res.success && res.data?.length) {
            setCategoryOptions(res.data)
          }
        })
        .catch(() => {
          // ignore
        })
    }
    if (!tagOptions.length) {
      getListTags()
        .then((res) => {
          if (res.success && res.data?.length) {
            setTagOptions(res.data)
          }
        })
        .catch(() => {
          // ignore
        })
    }
  }, [categoryOptions.length, tagOptions.length])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVisibility(list.private ? 'private' : 'public')
  }, [list.private])

  const handleEditListItem = (index: number, title?: string, metric?: string) => {
    const nextItems = (editList.items || []).slice()
    const item = nextItems[index]
    if (!item) return
    nextItems[index] = {
      ...item,
      title: title ?? item.title,
      metric: metric ?? item.metric,
    }

    setEditList({
      ...editList,
      items: nextItems,
    })
  }

  const handleEditListRank = (currentIndex: number, newIndex: number) => {
    if (!editList.items?.length) return
    const newList = editList.items.slice()
    if (newIndex <= 0 || newIndex > newList.length) return
    const itemIndex = newList.findIndex((item) => item.index === currentIndex)
    const newItemIndex = newList.findIndex((item) => item.index === newIndex)
    if (itemIndex < 0 || newItemIndex < 0) return

    const item = newList[itemIndex]
    const newItem = newList[newItemIndex]
    if (!item || !newItem) return

    item.index = newIndex
    newItem.index = currentIndex

    newList[newItemIndex] = item
    newList[itemIndex] = newItem

    setEditList({
      ...editList,
      items: newList.slice().sort((a, b) => (a.index || 0) - (b.index || 0)),
    })
  }

  const handleSaveList = () => {
    if (!editList?.id || saving) return
    setSaving(true)
    saveList(
      editList.id,
      editList.prompt,
      editList.items,
      editList.category ? split(editList.category, ',') : [],
      editList.tags ? split(editList.tags, ',') : [],
      visibility === 'private'
    )
      .then((res) => {
        if (res.success && res.data) {
          let savedList = editList
          if (typeof res.data === 'object' && res.data !== null) {
            const nextList = res.data as ListType
            setEditList(nextList)
            savedList = nextList
            if (nextList.private) {
              setVisibility('private')
            }
          }
          setSavedMsg('Saved!')
          onSaved?.(savedList)
          setTimeout(() => setSavedMsg(''), 2000)
        } else {
          setSaveAlert({
            open: true,
            severity: 'error',
            message: res.message || 'Unable to save this list. Please try again.',
          })
        }
      })
      .catch(() =>
        setSaveAlert({
          open: true,
          severity: 'error',
          message: 'Unable to save this list. Please try again.',
        })
      )
      .finally(() => setSaving(false))
  }

  const handleDeleteList = () => {
    if (!editList?.id) return
    deleteList(editList.id)
      .then((res) => {
        if (res.success && editList.id) {
          onDeleted?.(editList.id)
        }
      })
      .catch(() => {
        //
      })
  }

  return (
    <Box sx={{ pt: 0 }}>
      <EditorGrid
        className="editor-with-nav editor-trivia-details"
        sx={{
          '& .editor-nav': { mb: { xs: '-6px', sm: '-10px' } },
          '& .editor-title': {
            background: '#193b58',
            borderColor: '#335770',
            borderRadius: '28px',
            boxShadow: '0 4px 0 #bfd4e3',
            position: 'relative',
            overflow: 'hidden',
            isolation: 'isolate',
            '& > *': { position: 'relative', zIndex: 1 },
            '&::before': {
              content: '""',
              position: 'absolute',
              width: 240,
              height: 240,
              right: -95,
              top: -115,
              borderRadius: '50%',
              border: '32px solid #b9d4e512',
              zIndex: 0,
              pointerEvents: 'none',
            },
            '& .MuiOutlinedInput-root:not(.Mui-disabled)': { background: '#315570', color: '#fff' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#587991' },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
              { borderColor: '#bfd4e3' },
          },
          '& .editor-main > *, & .editor-actions': {
            background: '#fff',
            borderColor: '#d6e4ef',
            boxShadow: '0 3px 0 #d6e4ef',
            borderRadius: '24px',
          },
          '& .editor-meta': {
            background: '#eaf3f8',
            borderColor: '#d6e4ef',
            boxShadow: '0 3px 0 #bfd4e3',
          },
          '& .editor-main h2, & .editor-meta h2': {
            fontFamily: displayFont,
            fontWeight: 600,
            fontSize: { xs: 23, sm: 26 },
            color: '#193b58',
          },
        }}
      >
        <Box className="editor-nav">
          <ViewNavigation mb={0}>
            <GlassOutlineButton
              variant="outlined"
              onClick={onClose}
              sx={{
                pl: 2,
                pr: 3,
                py: 0.75,
                color: '#60768b',
                fontWeight: 600,
                boxShadow: shadow1,
              }}
              startIcon={<ArrowBackRoundedIcon fontSize="inherit" sx={{ color: '#7c91a3' }} />}
            >
              Trivia
            </GlassOutlineButton>
            <GlassOutlineButton
              variant="outlined"
              onClick={onOpenHowToPlay}
              sx={{
                px: 3,
                py: 0.75,
                color: '#60768b',
                fontWeight: 600,
                boxShadow: shadow1,
                minWidth: '140px',
                whiteSpace: 'nowrap',
              }}
              startIcon={<InfoOutlinedIcon fontSize="inherit" sx={{ color: '#7c91a3' }} />}
            >
              How to Play
            </GlassOutlineButton>
          </ViewNavigation>
        </Box>
        <Box className="editor-title">
          <Stack spacing={2} sx={{ textAlign: 'left' }}>
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <Typography
                variant="overline"
                sx={{ color: '#b9d4e5', letterSpacing: 1.2, fontWeight: 700 }}
              >
                TRIVIA EDITOR
              </Typography>
              <Chip
                size="small"
                label={
                  forcedPrivateByModeration || visibility === 'private'
                    ? 'Private list'
                    : 'Public list'
                }
                sx={{ background: '#315570', color: '#e1f5fb', border: '1px solid #587991' }}
              />
            </Box>
            <Box display="flex" gap={2} alignItems="center">
              <Box
                component="img"
                src="/brand/podium.svg"
                alt=""
                aria-hidden
                sx={{ width: { xs: 60, sm: 84 }, flexShrink: 0 }}
              />
              <Box minWidth={0}>
                <Typography
                  variant="h2"
                  sx={{ color: '#fff', fontSize: { xs: 27, sm: 36 }, textAlign: 'left' }}
                >
                  Edit your trivia.
                </Typography>
                <Typography variant="body2" sx={{ color: '#b9d4e5', mt: 0.75, textAlign: 'left' }}>
                  Fine-tune the facts, ranking, and who can discover your list.
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography
                component="label"
                htmlFor="edit-trivia-title"
                variant="body2"
                sx={{ display: 'block', color: '#e1f5fb', fontWeight: 600, mb: 1 }}
              >
                Title / prompt
              </Typography>
              <TextField
                id="edit-trivia-title"
                disabled={saving}
                fullWidth
                value={editList.prompt || ''}
                onChange={(e) => setEditList({ ...editList, prompt: e.target.value })}
              />
            </Box>
            <Box display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap={1}>
              {[
                ['Plays', editList.plays || 0],
                ['Likes', editList.ratesUp || 0],
                ['Items', editList.items?.length || 0],
              ].map(([label, value]) => (
                <Box
                  key={label}
                  sx={{
                    p: 1.25,
                    background: '#315570',
                    border: '1px solid #445f77',
                    borderRadius: '14px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 20,
                      color: '#fff',
                      fontWeight: 800,
                      lineHeight: 1.2,
                      textAlign: 'left',
                    }}
                  >
                    {value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#b9d4e5' }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>
        </Box>
        <Box className="editor-meta">
          <GlassPanel
            sx={{
              p: { xs: 1, sm: 2 },
              background: '#e1f5fb',
            }}
          >
            <Stack spacing={1}>
              <EditorSectionHeading
                title="Discoverability"
                description="Help the right people find it."
              />
              <Stack spacing={1}>
                <Autocomplete
                  disabled={saving}
                  size="small"
                  onChange={(_e, v) => {
                    const next = normalizeMetaList(v)
                    if (next.join(',').length > 255) {
                      return
                    }
                    setEditList({
                      ...editList,
                      category: next.join(','),
                    })
                  }}
                  value={selectedCategories}
                  freeSolo={true}
                  multiple={true}
                  options={mergedCategoryOptions}
                  sx={{
                    background: '#f7fbfe',
                    borderRadius: '14px',
                  }}
                  renderInput={({ InputProps, ...params }) => (
                    <TextField
                      InputProps={{
                        ...InputProps,
                        ...{
                          sx: {
                            background: '#ffffff',
                            borderRadius: '14px',
                          },
                        },
                      }}
                      {...params}
                      label="Categories"
                      placeholder="Categories"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  )}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({
                        index,
                      })
                      return (
                        <Chip
                          size="small"
                          variant="filled"
                          label={normalizeMetaValue(option)}
                          key={key}
                          deleteIcon={
                            <ClearRoundedIcon
                              fontSize="inherit"
                              sx={{ fontSize: '13px !important' }}
                            />
                          }
                          sx={{
                            background: mainColor,
                            color: mainColorMediumText,
                            fontWeight: 600,
                            boxShadow: shadow1,
                          }}
                          {...tagProps}
                        />
                      )
                    })
                  }
                />
                <Autocomplete
                  disabled={saving}
                  size="small"
                  value={selectedTags}
                  onChange={(_e, v) => {
                    const next = normalizeMetaList(v)
                    if (next.join(',').length > 255) {
                      return
                    }
                    setEditList({
                      ...editList,
                      tags: next.join(','),
                    })
                  }}
                  freeSolo
                  multiple={true}
                  options={mergedTagOptions}
                  renderInput={({ InputProps, ...params }) => (
                    <TextField
                      InputProps={{
                        ...InputProps,
                        ...{
                          sx: {
                            background: '#f7fbfe',
                            borderRadius: '12px',
                          },
                        },
                      }}
                      {...params}
                      label="Tags"
                      placeholder="Tags"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  )}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({
                        index,
                      })
                      return (
                        <Chip
                          size="small"
                          variant="filled"
                          label={normalizeMetaValue(option)}
                          key={key}
                          deleteIcon={
                            <ClearRoundedIcon
                              fontSize="inherit"
                              sx={{ fontSize: '13px !important' }}
                            />
                          }
                          sx={{
                            background: altColor,

                            color: altColorDarkText,
                            fontWeight: 600,
                            boxShadow: shadow1,
                          }}
                          {...tagProps}
                        />
                      )
                    })
                  }
                />

                <Stack>
                  <TextField
                    select
                    fullWidth
                    label="Visibility"
                    value={visibility}
                    onChange={(e) => {
                      const next = e.target.value as 'public' | 'private'
                      setVisibility(next)
                      setEditList((prev) => ({
                        ...prev,
                        private: next === 'private',
                      }))
                    }}
                    disabled={saving || forcedPrivateByModeration}
                    InputProps={{
                      sx: {
                        background: '#f7fbfe',
                        borderRadius: '14px',
                      },
                    }}
                    size="small"
                  >
                    <MenuItem value="public">Public</MenuItem>
                    <MenuItem value="private">Private</MenuItem>
                  </TextField>
                  <Typography variant="caption" color={helperTextColor} textAlign="left" pl={1}>
                    {forcedPrivateByModeration
                      ? "This list can't be set to public."
                      : visibility === 'public'
                        ? 'Visible and playable by the community.'
                        : 'Private and only shareable by link.'}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </GlassPanel>
        </Box>
        <Box className="editor-main">
          <GlassPanel
            sx={{
              p: { xs: 2, sm: 3 },
              background: '#ffffff',
            }}
          >
            <Stack spacing={2}>
              <EditorSectionHeading
                title="Ranked items"
                description="Keep items in their correct factual order."
              />
              <Stack spacing={1}>
                {editList.items?.map((item, i) => (
                  <GlassCard
                    key={`${item.index}-${i}`}
                    sx={{
                      px: 1.5,
                      py: 1.5,
                      borderRadius: '16px',
                      borderColor: '#e0eaf2',
                      boxShadow: 'none',
                      background: '#f7fbfe',
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.25}>
                      <Box
                        sx={{
                          minWidth: 30,
                          height: 30,
                          borderRadius: '10px',
                          backgroundColor: i < 3 ? '#193b58' : '#eaf3f8',
                          color: i < 3 ? '#fff' : '#193b58',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {i + 1}
                      </Box>
                      <Box flexGrow={1} minWidth={0}>
                        <TextField
                          disabled={saving}
                          fullWidth
                          size="small"
                          inputProps={{ 'aria-label': `Item ${i + 1} title` }}
                          value={item.title}
                          onChange={(e) => {
                            handleEditListItem(i, e.target.value, undefined)
                          }}
                          InputProps={{
                            sx: {
                              background: '#f7fbfe',
                              borderRadius: '12px',
                            },
                          }}
                        />
                        <TextField
                          disabled={saving}
                          fullWidth
                          variant="standard"
                          size="small"
                          placeholder="metric (optional)"
                          inputProps={{ 'aria-label': `Item ${i + 1} metric` }}
                          value={item.metric || ''}
                          onChange={(e) => {
                            handleEditListItem(i, undefined, e.target.value)
                          }}
                          sx={{ mt: 0.5 }}
                          InputProps={{
                            disableUnderline: true,
                            sx: {
                              borderRadius: '12px',
                              fontSize: '13px',
                              background: '#f7fbfe',
                              px: 1.5,
                              color: '#60768b',
                            },
                          }}
                        />
                      </Box>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <IconButton
                          size="small"
                          aria-label={`Move item ${i + 1} up`}
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '10px',
                            background: '#eaf3f8',
                            color: '#193b58',
                          }}
                          disabled={saving || i === 0}
                          onClick={() => {
                            handleEditListRank(item.index || i + 1, (item.index || i + 1) - 1)
                          }}
                        >
                          <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={`Move item ${i + 1} down`}
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '10px',
                            background: '#eaf3f8',
                            color: '#193b58',
                          }}
                          disabled={saving || i === (editList.items?.length || 0) - 1}
                          onClick={() => {
                            handleEditListRank(item.index || i + 1, (item.index || i + 1) + 1)
                          }}
                        >
                          <KeyboardArrowDownIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </GlassCard>
                ))}
              </Stack>
            </Stack>
          </GlassPanel>
        </Box>
        <Box className="editor-actions">
          <Box
            display="flex"
            justifyContent="space-between"
            gap={1}
            flexDirection={{
              xs: 'column-reverse',
              sm: 'row',
            }}
          >
            <Button
              disabled={saving}
              variant="text"
              size="large"
              color="error"
              onClick={() => setDeleteOpen(true)}
              sx={{
                px: 2,
                borderRadius: '22px',
              }}
              endIcon={<DeleteForeverIcon />}
            >
              <Typography variant="body1" fontWeight={500}>
                Delete
              </Typography>
            </Button>

            <GlassButton
              disabled={saving || !editList.prompt.trim()}
              variant="contained"
              size="large"
              sx={{
                minWidth: { xs: '100%', sm: 180 },
                '&:not(.Mui-disabled)': {
                  background: '#193b58',
                  color: '#fff',
                  borderColor: '#335770',
                  boxShadow: '0 4px 0 #bfd4e3',
                  '&:hover': { background: '#193b58', boxShadow: '0 4px 0 #bfd4e3' },
                  '&:active': { boxShadow: '0 2px 0 #bfd4e3' },
                },
              }}
              endIcon={<SaveIcon />}
              onClick={handleSaveList}
            >
              <Typography variant="body1" fontWeight={700}>
                {saving ? 'Saving…' : savedMsg || 'Save changes'}
              </Typography>
            </GlassButton>
          </Box>
        </Box>
      </EditorGrid>

      <Alert alert={saveAlert} onClose={() => setSaveAlert({ open: false })} />
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        sx={{
          backdropFilter: 'none',
          '& .MuiDialog-paper': {
            borderRadius: '32px',
            p: 1,
          },
        }}
      >
        <DialogTitle>Delete List?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="#64748b" component="div">
            This will permanently delete this list and its items. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Button
              variant="text"
              onClick={() => setDeleteOpen(false)}
              sx={{
                borderRadius: '32px',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setDeleteOpen(false)
                handleDeleteList()
              }}
              sx={{
                borderRadius: '32px',
              }}
            >
              Delete
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default EditListView
